import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './ManageAddresses.css';
import { API, authHeadersOnly } from '../services/apiConfig';

function ManageAddresses({ userId, onClose }) {
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    isDefaultShipping: false
  });

  const emptyForm = { fullName: '', phone: '', addressLine1: '', addressLine2: '', city: '', state: '', pincode: '', isDefaultShipping: false };
  const errStyle = { color: '#c0392b', fontSize: '12px', margin: '-4px 0 4px' };

  const validateAddress = () => {
    const e = {};
    if (!formData.fullName.trim()) e.fullName = 'Full name is required';
    if (!/^[0-9]{10}$/.test(formData.phone)) e.phone = 'Phone must be exactly 10 digits';
    if (!formData.addressLine1.trim()) e.addressLine1 = 'Address is required';
    if (!formData.city.trim()) e.city = 'City is required';
    if (!formData.state.trim()) e.state = 'State is required';
    if (!/^[0-9]{6}$/.test(formData.pincode)) e.pincode = 'Pincode must be exactly 6 digits';
    return e;
  };

  useEffect(() => {
    fetchAddresses();
  }, []);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/api/shipping-address/user/${userId}`, { headers: authHeadersOnly() });
      setAddresses(response.data);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateAddress();
    if (Object.keys(validationErrors).length > 0) { setErrors(validationErrors); return; }
    setErrors({});
    setSubmitError('');
    try {
      await axios.post(`${API}/api/shipping-address`, { userId, ...formData }, { headers: authHeadersOnly() });
      setSubmitSuccess('Address saved successfully!');
      setShowAddForm(false);
      setFormData(emptyForm);
      fetchAddresses();
      setTimeout(() => setSubmitSuccess(''), 3000);
    } catch (error) {
      setSubmitError(error.response?.data?.message || 'Failed to save address. Please check your details and try again.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await axios.delete(`${API}/api/shipping-address/${id}`, { headers: authHeadersOnly() });
      fetchAddresses();
    } catch (error) {
      setSubmitError('Failed to delete address. Please try again.');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await axios.put(`${API}/api/shipping-address/${id}/default?userId=${userId}`, null, { headers: authHeadersOnly() });
      fetchAddresses();
    } catch (error) {
      setSubmitError('Failed to update default address. Please try again.');
    }
  };

  return (
    <div className="manage-addresses-overlay" onClick={onClose}>
      <div className="manage-addresses-modal" onClick={(e) => e.stopPropagation()}>
        <div className="addresses-header">
          <h2>Your Addresses</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="addresses-content">
          {submitSuccess && <p style={{ color: '#16a34a', fontSize: '13px', marginBottom: '8px', fontWeight: '600' }}>{submitSuccess}</p>}
          {submitError && <p style={{ color: '#c0392b', fontSize: '13px', marginBottom: '8px' }}>{submitError}</p>}
          {!showAddForm && (
            <button className="btn-add-address" onClick={() => setShowAddForm(true)}>
              + Add New Address
            </button>
          )}

          {showAddForm && (
            <form className="address-form" onSubmit={handleSubmit}>
              <h3>Add New Address</h3>
              <input
                type="text"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={(e) => { setFormData({...formData, fullName: e.target.value}); setErrors(p=>({...p,fullName:''})); }}
                required
              />
              {errors.fullName && <p style={errStyle}>{errors.fullName}</p>}
              <input
                type="tel"
                placeholder="Phone Number (10 digits)"
                value={formData.phone}
                onKeyDown={(e) => {
                  if (!/[0-9]/.test(e.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab'].includes(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setFormData({...formData, phone: val});
                  setErrors(p=>({...p,phone:''}));
                }}
                maxLength="10"
                required
              />
              {errors.phone && <p style={errStyle}>{errors.phone}</p>}
              <input
                type="text"
                placeholder="Address Line 1"
                value={formData.addressLine1}
                onChange={(e) => { setFormData({...formData, addressLine1: e.target.value}); setErrors(p=>({...p,addressLine1:''})); }}
                required
              />
              {errors.addressLine1 && <p style={errStyle}>{errors.addressLine1}</p>}
              <input
                type="text"
                placeholder="Address Line 2 (Optional)"
                value={formData.addressLine2}
                onChange={(e) => setFormData({...formData, addressLine2: e.target.value})}
              />
              <div className="form-row">
                <div>
                  <input
                    type="text"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => { setFormData({...formData, city: e.target.value}); setErrors(p=>({...p,city:''})); }}
                    required
                  />
                  {errors.city && <p style={errStyle}>{errors.city}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="State"
                    value={formData.state}
                    onChange={(e) => { setFormData({...formData, state: e.target.value}); setErrors(p=>({...p,state:''})); }}
                    required
                  />
                  {errors.state && <p style={errStyle}>{errors.state}</p>}
                </div>
                <div>
                  <input
                    type="text"
                    placeholder="Pincode (6 digits)"
                    value={formData.pincode}
                    onKeyDown={(e) => {
                      if (!/[0-9]/.test(e.key) && !['Backspace','Delete','ArrowLeft','ArrowRight','Tab'].includes(e.key)) e.preventDefault();
                    }}
                    onChange={(e) => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setFormData({...formData, pincode: val});
                      setErrors(p=>({...p,pincode:''}));
                    }}
                    maxLength="6"
                    required
                  />
                  {errors.pincode && <p style={errStyle}>{errors.pincode}</p>}
                </div>
              </div>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isDefaultShipping}
                  onChange={(e) => setFormData({...formData, isDefaultShipping: e.target.checked})}
                />
                Set as default address
              </label>
              <div className="form-actions">
                <button type="submit" className="btn-save">Save Address</button>
                <button type="button" className="btn-cancel" onClick={() => { setShowAddForm(false); setErrors({}); }}>Cancel</button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="loading">Loading addresses...</div>
          ) : addresses.length === 0 ? (
            <div className="empty-state">
              <p>No saved addresses yet</p>
            </div>
          ) : (
            <div className="addresses-list">
              {addresses.map((address) => (
                <div key={address.id} className={`address-card ${address.isDefaultShipping ? 'default' : ''}`}>
                  {address.isDefaultShipping && <span className="default-badge">Default</span>}
                  <h4>{address.fullName}</h4>
                  <p>{address.addressLine1}</p>
                  {address.addressLine2 && <p>{address.addressLine2}</p>}
                  <p>{address.city}, {address.state} - {address.pincode}</p>
                  <p>Phone: {address.phone}</p>
                  <div className="address-actions">
                    {!address.isDefaultShipping && (
                      <button onClick={() => handleSetDefault(address.id)}>Set as Default</button>
                    )}
                    <button onClick={() => handleDelete(address.id)} className="btn-delete">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ManageAddresses;
